/**
 * Styled Menu Selector
 * Replaces native HTML select elements with a menu-style dropdown
 * matching the ENG-2279 checkbox-menu visual language (single-select).
 */
import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    Icon,
    HStack,
    Flex,
    Portal,
    Button,
} from '@chakra-ui/react';
import { TbChevronDown } from 'react-icons/tb';
import {
    CheckboxMenuTrigger,
    CheckboxMenuList,
    checkboxMenuPopoverContentSx,
    checkboxMenuColors,
    checkboxMenuFocusRing,
} from '../../../ui/primitives/CheckboxMenu';

export interface PopoverOption {
    value: string;
    label: string;
}

interface PopoverSelectorProps {
    options: PopoverOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    width?: string;
    size?: 'sm' | 'md';
    isDisabled?: boolean;
}

const PopoverSelectorMd = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    width = '170px',
    isDisabled = false,
}: Omit<PopoverSelectorProps, 'size'>) => {
    const selectedOption = options.find(o => o.value === value);
    const displayLabel = selectedOption?.label || placeholder;
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);

    useEffect(() => {
        if (isOpen && triggerRef.current) {
            setPopoverWidth(triggerRef.current.offsetWidth);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isDisabled && isOpen) setIsOpen(false);
    }, [isDisabled, isOpen]);

    return (
        <Box w={width}>
            <Popover
                placement="bottom-start"
                isOpen={isOpen}
                onOpen={() => {
                    if (!isDisabled) setIsOpen(true);
                }}
                onClose={() => setIsOpen(false)}
                gutter={8}
            >
                <PopoverTrigger>
                    <Box ref={triggerRef}>
                        <CheckboxMenuTrigger
                            isOpen={isOpen}
                            tokens={value ? [displayLabel] : []}
                            emptyLabel={placeholder}
                        />
                    </Box>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent
                        w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth}
                        zIndex={1500}
                        onClick={e => e.stopPropagation()}
                        sx={checkboxMenuPopoverContentSx}
                    >
                        <PopoverBody p={0}>
                            <CheckboxMenuList>
                                {options.map(option => {
                                    const isSelected = option.value === value;
                                    return (
                                        <Flex
                                            key={option.value}
                                            align="center"
                                            h="34px"
                                            px="10px"
                                            borderRadius="7px"
                                            cursor="pointer"
                                            bg={isSelected ? checkboxMenuColors.selectedRowBg : 'transparent'}
                                            _hover={{ bg: isSelected ? checkboxMenuColors.selectedRowBg : checkboxMenuColors.hoverRowBg }}
                                            onClick={() => {
                                                onChange(option.value);
                                                setIsOpen(false);
                                            }}
                                        >
                                            <Text
                                                flex={1}
                                                fontSize="13px"
                                                lineHeight="13px"
                                                color={isSelected ? checkboxMenuColors.selectedLabel : checkboxMenuColors.rowLabel}
                                                fontWeight={isSelected ? 600 : 400}
                                                noOfLines={1}
                                            >
                                                {option.label}
                                            </Text>
                                        </Flex>
                                    );
                                })}
                            </CheckboxMenuList>
                        </PopoverBody>
                    </PopoverContent>
                </Portal>
            </Popover>
        </Box>
    );
};

const PopoverSelector = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    width = '170px',
    size = 'sm',
    isDisabled = false,
}: PopoverSelectorProps) => {
    if (size === 'md') {
        return (
            <PopoverSelectorMd
                options={options}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                width={width}
                isDisabled={isDisabled}
            />
        );
    }

    const selectedOption = options.find(o => o.value === value);
    const displayLabel = selectedOption?.label || placeholder;

    return (
        <Box w={width}>
            <Menu placement="bottom-end" matchWidth isLazy>
                <MenuButton
                    as={Button}
                    variant="outline"
                    bg="white"
                    borderColor={checkboxMenuColors.popoverBorder}
                    fontWeight="normal"
                    size="sm"
                    h="24px"
                    px={3}
                    w="100%"
                    textAlign="left"
                    isDisabled={isDisabled}
                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} color={checkboxMenuColors.icon} />}
                    _hover={{ borderColor: checkboxMenuColors.brand }}
                    _active={{ bg: 'white' }}
                    _focusVisible={{ borderColor: checkboxMenuColors.brand, boxShadow: checkboxMenuFocusRing }}
                >
                    <Text fontSize="xs" noOfLines={1} color={value ? checkboxMenuColors.selectedLabel : checkboxMenuColors.muted}>
                        {displayLabel}
                    </Text>
                </MenuButton>
                <MenuList
                    py={0}
                    px="6px"
                    zIndex={2000}
                    maxH="236px"
                    overflowY="auto"
                    borderColor={checkboxMenuColors.popoverBorder}
                    borderRadius="10px"
                    boxShadow="none"
                >
                    {options.map(option => {
                        const isSelected = option.value === value;
                        return (
                            <MenuItem
                                key={option.value}
                                onClick={() => onChange(option.value)}
                                fontSize="13px"
                                h="34px"
                                px="10px"
                                borderRadius="7px"
                                fontWeight={isSelected ? 600 : 400}
                                color={isSelected ? checkboxMenuColors.selectedLabel : checkboxMenuColors.rowLabel}
                                bg={isSelected ? checkboxMenuColors.selectedRowBg : 'transparent'}
                                _hover={{ bg: isSelected ? checkboxMenuColors.selectedRowBg : checkboxMenuColors.hoverRowBg, color: isSelected ? checkboxMenuColors.selectedLabel : checkboxMenuColors.rowLabel }}
                            >
                                <HStack spacing={3} w="100%" justify="space-between">
                                    <Text>{option.label}</Text>
                                </HStack>
                            </MenuItem>
                        );
                    })}
                </MenuList>
            </Menu>
        </Box>
    );
};

export default PopoverSelector;
